import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, ArrowRight, Copy } from "iconoir-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ForgotPassword() {
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [devLink, setDevLink] = useState(null);
    const [emailSent, setEmailSent] = useState(false);
    const [providerHint, setProviderHint] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
            setSubmitted(true);
            setEmailSent(!!data.email_sent);
            setProviderHint(data.provider_hint || null);
            if (data.dev_reset_link && !data.email_sent) {
                setDevLink(data.dev_reset_link);
            }
        } catch {
            toast.error("Try again in a moment");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="auth-bg min-h-screen w-full flex items-center justify-center p-5">
            <div className="auth-sun" />
            <div className="auth-heart" />
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-md"
            >
                <div className="flex flex-col items-center text-center mb-6">
                    <img src="/milo-logo.png" alt="Milo" className="h-14 w-auto object-contain mb-6" />
                    <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.02]">
                        Reset password
                    </h1>
                    <p className="text-base text-[#255636] mt-3">We'll send you a reset link.</p>
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-[28px] p-6 sm:p-7 shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.08)] border border-white/40">
                    {!submitted ? (
                        <form onSubmit={submit} className="space-y-4">
                            <div>
                                <div className="mb-2 flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                                        <Mail className="w-3.5 h-3.5 text-[#2F6B43]" />
                                    </div>
                                    <span className="text-sm font-semibold text-[#0d2a1b]">Email</span>
                                </div>
                                <input
                                    data-testid="forgot-email-input"
                                    type="email"
                                    required
                                    className="brut-input"
                                    placeholder="you@family.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="forgot-submit-btn"
                                className="w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3.5 font-semibold text-base hover:bg-[#255636] transition-colors shadow-[0_6px_20px_rgba(47,107,67,0.3)] disabled:opacity-70"
                            >
                                {busy ? "Sending…" : "Send reset link"} <ArrowRight className="w-4 h-4" />
                            </button>
                        </form>
                    ) : (
                        <div className="space-y-3 text-center">
                            <div className="font-display text-2xl font-semibold">Check your email</div>
                            {providerHint === "google" ? (
                                <>
                                    <div className="text-sm text-[#255636]">
                                        This account uses <b>Google sign-in</b> — we sent you a link to continue with Google.
                                    </div>
                                    <button
                                        data-testid="go-to-google-login-btn"
                                        onClick={() => nav("/login")}
                                        className="w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3 font-semibold text-sm hover:bg-[#255636] transition-colors mt-2"
                                    >
                                        Sign in with Google
                                    </button>
                                </>
                            ) : (
                                <div className="text-sm text-[#255636]">
                                    If an account exists for <b>{email}</b>, a reset link is on its way.
                                </div>
                            )}
                            {devLink && (
                                <div className="bg-[#fbeecb] rounded-2xl p-3 text-left mt-4">
                                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1 text-[#7a5c1a]">
                                        Dev mode (email not configured)
                                    </div>
                                    <button
                                        data-testid="dev-reset-link-btn"
                                        onClick={() => nav(devLink)}
                                        className="brut-btn brut-btn-secondary text-sm w-full justify-center mt-2"
                                    >
                                        <Copy className="w-3.5 h-3.5" /> Open reset link
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <Link
                        to="/login"
                        className="mt-4 flex items-center justify-center gap-2 text-sm font-semibold text-[#2F6B43] hover:underline"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to sign in
                    </Link>
                </div>
            </motion.div>
        </div>
    );
}
